<template>
  <v-container>
    <v-card class="elevation-4 rounded-lg">
      <v-card-title class="headline white--text pa-4" style="background-color: #005f32;">
        Email Actions Configuration
        <v-spacer></v-spacer>
        <v-btn color="white" light elevation="2" class="font-weight-bold" @click="openDialog()">
          <v-icon left color="#005f32">mdi-plus</v-icon> <span style="color: #005f32">Add Action</span>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-4">
        <v-row class="mb-2">
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search Actions"
              single-line
              hide-details
              outlined
              dense
            ></v-text-field>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="actions"
          :search="search"
          :loading="loading"
          class="elevation-0 mt-4"
          :page.sync="page"
          @page-count="pageCount = $event"
          hide-default-footer
        >
          <template v-slot:item.body="{ item }">
            <div class="text-truncate" style="max-width: 300px;">
              {{ stripHtml(item.body) }}
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon small class="mr-2" color="blue" @click="editAction(item)">
              mdi-pencil
            </v-icon>
            <v-icon small color="red" @click="deleteAction(item._id)">
              mdi-delete
            </v-icon>
          </template>
        </v-data-table>
        <div class="text-center pt-4 pb-2" v-if="pageCount > 1">
          <v-pagination
            v-model="page"
            :length="pageCount"
            color="#005f32"
          ></v-pagination>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialog" max-width="800px">
      <v-card>
        <v-card-title class="headline" style="background-color: #005f32; color: white;">
          {{ editedIndex > -1 ? 'Edit Action' : 'New Action' }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.name"
                  label="Action Name (e.g., Welcome Email)"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.subject"
                  label="Email Subject"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <p class="mb-1 text-subtitle-1">Email Body</p>
                <vue-editor
                  v-model="editedItem.body"
                ></vue-editor>
                <p class="text-caption grey--text mt-2" v-pre>Use {{name}} to auto-populate the employee's name.</p>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeDialog">Cancel</v-btn>
          <v-btn color="#005f32" class="white--text" @click="saveAction">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snackbar" :color="snackbarColor" top right :timeout="3000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import { VueEditor } from "vue2-editor";

export default {
  name: "EmailActions",
  components: {
    VueEditor,
  },
  data() {
    return {
      headers: [
        { text: "Action Name", value: "name" },
        { text: "Subject", value: "subject" },
        { text: "Body Preview", value: "body" },
        { text: "Actions", value: "actions", sortable: false, align: "right" },
      ],
      actions: [],
      search: "",
      page: 1,
      pageCount: 0,
      loading: false,
      dialog: false,
      editedIndex: -1,
      editedItem: {
        name: "",
        subject: "",
        body: "",
      },
      defaultItem: {
        name: "",
        subject: "",
        body: "",
      },
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
    };
  },
  created() {
    this.fetchActions();
  },
  methods: {
    showToast(message, color = "success") {
      this.snackbarText = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    stripHtml(html) {
      if (!html) return '';
      return html.replace(/<[^>]*>?/gm, '');
    },
    async fetchActions() {
      this.loading = true;
      try {
        const response = await axios.get(
          "/admin/email-actions/all",
          {
            headers: {
              "x-auth-token": localStorage.getItem("token"),
            },
          }
        );
        this.actions = response.data;
      } catch (error) {
        console.error("Error fetching actions", error);
        this.showToast("Failed to fetch email actions", "error");
      }
      this.loading = false;
    },
    openDialog() {
      this.editedItem = Object.assign({}, this.defaultItem);
      this.editedIndex = -1;
      this.dialog = true;
    },
    editAction(item) {
      this.editedIndex = this.actions.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    async saveAction() {
      if (!this.editedItem.name || !this.editedItem.subject || !this.editedItem.body) {
        return this.showToast("All fields are required", "error");
      }

      try {
        if (this.editedIndex > -1) {
          // Update
          await axios.put(
            "/admin/email-actions/update/" + this.editedItem._id,
            this.editedItem,
            {
              headers: { "x-auth-token": localStorage.getItem("token") },
            }
          );
          this.showToast("Action updated successfully");
        } else {
          // Create
          await axios.post(
            "/admin/email-actions/create",
            this.editedItem,
            {
              headers: { "x-auth-token": localStorage.getItem("token") },
            }
          );
          this.showToast("Action created successfully");
        }
        this.closeDialog();
        this.fetchActions();
      } catch (error) {
        console.error("Error saving action", error);
        this.showToast((error.response && error.response.data && error.response.data.error) || "Failed to save action", "error");
      }
    },
    async deleteAction(id) {
      if (confirm("Are you sure you want to delete this action?")) {
        try {
          await axios.delete(
            "/admin/email-actions/delete/" + id,
            {
              headers: { "x-auth-token": localStorage.getItem("token") },
            }
          );
          this.showToast("Action deleted successfully");
          this.fetchActions();
        } catch (error) {
          console.error("Error deleting action", error);
          this.showToast("Failed to delete action", "error");
        }
      }
    },
  },
};
</script>

<style scoped>
</style>
